import React, { memo, useCallback, useState } from 'react'

import './DemoForm.scss'

import { useAuth } from '../../contexts/AuthContext'
import { LabeledField } from '../LabeledField'
import { DemoFormFields } from './DemoFormFields'
import {
  type DemoFormValues,
  EMPTY_DEMO_FORM_VALUES,
} from './types'

export interface CreateDemoFormProps {
  onCreated: () => Promise<void>
  onClose: () => void
}

export const CreateDemoForm = memo(function CreateDemoForm({
  onCreated,
  onClose,
}: CreateDemoFormProps) {
  const [values, setValues] = useState<DemoFormValues>(EMPTY_DEMO_FORM_VALUES)
  const [clientName, setClientName] = useState('')
  const [password, setPassword] = useState('')
  const [layerClientId, setLayerClientId] = useState('')
  const [layerClientSecret, setLayerClientSecret] = useState('')
  const { authFetch } = useAuth()

  const handleChange = useCallback(
    <K extends keyof DemoFormValues>(key: K, value: DemoFormValues[K]) => {
      setValues(prev => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      try {
        const response = await authFetch('/api/demos', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...values,
            clientName,
            password,
            businessId: values.businessId || null,
            ...(layerClientId || layerClientSecret
              ? { layerClientId, layerClientSecret }
              : {}),
          }),
        })

        if (response.ok) {
          alert('Demo created successfully')
          setValues(EMPTY_DEMO_FORM_VALUES)
          setClientName('')
          setPassword('')
          setLayerClientId('')
          setLayerClientSecret('')
          await onCreated()
        } else {
          const errorData = await response.json()
          alert(`Failed to create demo: ${errorData.message || 'Unknown error'}`)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to submit form:', error)
        alert('Failed to create demo due to network error')
      }
    },
    [
      authFetch,
      clientName,
      layerClientId,
      layerClientSecret,
      onCreated,
      password,
      values,
    ],
  )

  return (
    <div className='create-demo-form'>
      <div className='create-demo-form__header'>
        <h2>Create Demo</h2>
        <button className='modal-close-button' onClick={onClose}>
          &times;
        </button>
      </div>
      <form onSubmit={handleSubmit} className='demo-form'>
        <LabeledField label='Brand Name:'>
          <input
            type='text'
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            required
          />
        </LabeledField>
        <DemoFormFields values={values} onChange={handleChange} />
        <LabeledField label='Demo Password:'>
          <input
            type='text'
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </LabeledField>
        <LabeledField label='Layer Client ID Override:'>
          <input
            type='text'
            value={layerClientId}
            onChange={e => setLayerClientId(e.target.value)}
          />
        </LabeledField>
        <LabeledField label='Layer Client Secret Override:'>
          <input
            type='password'
            value={layerClientSecret}
            onChange={e => setLayerClientSecret(e.target.value)}
          />
        </LabeledField>
        <div style={{ textAlign: 'right' }}>
          <button className='admin-button' type='submit'>
            Create Demo
          </button>
        </div>
      </form>
    </div>
  )
})
